/**
 * PAWTENANT-BRANDED-PORTAL-HOSTS-LIVE-001
 *
 * THE canonical LIVE host map. One file owns every portal hostname, which
 * portal owns which route, and where a mis-hosted request may be sent.
 *
 * Runtime-agnostic on purpose — no `window`, no `import.meta`, no Node/Deno
 * globals — because BOTH the browser bundle (src/lib/subdomainConfig.ts,
 * src/lib/portalUrls.ts, src/components/feature/PortalHostGuard.tsx) and the
 * Vercel Edge Middleware (middleware.ts) import it, so the edge and the client
 * can never disagree about who owns a route.
 *
 * Mirrors the TEST design (pawtenant-test src/lib/portalHosts.ts, task
 * PAWTENANT-PORTAL-SUBDOMAIN-MIGRATION-TEST-001) with LIVE hostnames. TEST
 * hostnames never appear here, and the TEST repo never routes to these hosts.
 *
 * Deliberate LIVE differences from TEST:
 *   - /checkout, /continue and /r are owned by the PUBLIC host. Every LIVE
 *     generator builds them on pawtenant.com, so a portal host sends them back
 *     there instead of running a payment away from the site's geo gate,
 *     marketing tags and thank-you page.
 *   - The admin host also serves the provider pages (one Staff & Provider
 *     sign-in, as on LIVE today) and /my-orders for the admin "view as
 *     customer" preview, which needs the admin's own session.
 *   - Redirecting the OLD pawtenant.com portal addresses is a separate,
 *     staged switch (LEGACY_PORTAL_REDIRECTS) so the new hosts can be brought
 *     up and verified before any existing bookmark moves.
 */

export type PortalKey = "public" | "customer" | "admin" | "provider" | "partner";

/** LIVE hostnames — the only hosts this repository routes on. */
export const LIVE_PORTAL_HOSTS: Readonly<Record<PortalKey, string>> = {
  public: "pawtenant.com",
  customer: "customer.pawtenant.com",
  admin: "admin.pawtenant.com",
  provider: "provider.pawtenant.com",
  partner: "partner.pawtenant.com",
};

/**
 * Rollout stage for the OLD portal addresses on pawtenant.com.
 *   "off"       — pawtenant.com keeps serving every portal page (bring-up).
 *   "temporary" — pawtenant.com portal pages 307 to their portal host.
 *   "permanent" — the same, as 308, once routes and sign-in are proven.
 * Portal hosts always send a route they do not serve to its owner (307).
 * Rollback: set the previous stage and redeploy, or promote the previous
 * Vercel deployment.
 */
export type LegacyRedirectMode = "off" | "temporary" | "permanent";
export const LEGACY_PORTAL_REDIRECTS: LegacyRedirectMode = "off";

// ── Route ownership ──────────────────────────────────────────────────────────
// Matching is exact-or-path-segment-prefix: "/admin-orders" also owns
// "/admin-orders/live" but never "/admin-ordersomething". A path in no list is
// public.

/** Customer portal routes. */
export const CUSTOMER_PATHS: readonly string[] = ["/customer-login", "/my-orders"];

/** PawTenant staff routes (admin workspace, Company OS, go-live tool). */
export const ADMIN_PATHS: readonly string[] = [
  "/admin-login",
  "/admin-orders",
  "/admin-partners",
  "/admin-doctors",
  "/admin-guide",
  "/admin-chats",
  "/admin",
  "/company",
  "/go-live",
];

/** Licensed provider portal routes. */
export const PROVIDER_PATHS: readonly string[] = ["/provider-login", "/provider-portal"];

/** Partner organisation portal routes. */
export const PARTNER_PATHS: readonly string[] = ["/partner-portal"];

/**
 * SHARED routes — served by every host and redirected by none.
 *   /reset-password: Supabase returns recovery/invite credentials in the URL
 *     hash or ?code=; the server never sees a hash, so the page that receives
 *     the credential must render where the link landed.
 *   /account: /account/checkout issues a resume link with the session of the
 *     host it was opened on (customer portal or admin), then navigates to the
 *     absolute pawtenant.com checkout link the server returns.
 */
export const SHARED_PATHS: readonly string[] = ["/reset-password", "/account"];

/**
 * Routes a host serves in addition to the ones it owns.
 *   admin:    the provider pages (a provider who signs in at the Staff &
 *             Provider Portal stays signed in there) and /my-orders for the
 *             admin "view as customer" preview.
 *   provider: /admin-login, because /provider-login forwards to the unified
 *             sign-in (keeping ?returnUrl= as ?next=) on the same host.
 */
export const ALSO_SERVED: Readonly<Record<PortalKey, readonly string[]>> = {
  public: [],
  customer: [],
  admin: ["/provider-login", "/provider-portal", "/my-orders"],
  provider: ["/admin-login"],
  partner: [],
};

/** Paths the host router must never touch, on any host. */
export const NEVER_ROUTED_PREFIXES: readonly string[] = ["/api", "/_vercel", "/assets", "/functions"];

/** True when `pathname` is `base` or a path-segment child of it. */
export function pathOwnedBy(pathname: string, base: string): boolean {
  if (pathname === base) return true;
  return pathname.startsWith(base + "/");
}

function matchesAny(pathname: string, bases: readonly string[]): boolean {
  for (const base of bases) if (pathOwnedBy(pathname, base)) return true;
  return false;
}

/** Strip a trailing slash so "/my-orders/" and "/my-orders" classify alike. */
export function normalisePath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith("/")) return pathname.slice(0, -1);
  return pathname || "/";
}

export function isSharedPath(pathname: string): boolean {
  return matchesAny(normalisePath(pathname), SHARED_PATHS);
}

/** Infrastructure and static files (anything with a file extension). */
export function isNeverRoutedPath(pathname: string): boolean {
  if (matchesAny(pathname, NEVER_ROUTED_PREFIXES)) return true;
  return /\.[a-zA-Z0-9]+$/.test(pathname);
}

/** The portal that OWNS this path. Shared/infrastructure paths and "/" → null. */
export function ownerPortalForPath(pathname: string): PortalKey | null {
  const path = normalisePath(pathname);
  if (path === "/" || isNeverRoutedPath(path) || isSharedPath(path)) return null;
  if (matchesAny(path, ADMIN_PATHS)) return "admin";
  if (matchesAny(path, PROVIDER_PATHS)) return "provider";
  if (matchesAny(path, PARTNER_PATHS)) return "partner";
  if (matchesAny(path, CUSTOMER_PATHS)) return "customer";
  return "public";
}

/** Whether the host for `portal` serves `pathname` itself. */
export function servedOn(portal: PortalKey, pathname: string): boolean {
  const owner = ownerPortalForPath(pathname);
  if (owner === null || owner === portal) return true;
  return matchesAny(normalisePath(pathname), ALSO_SERVED[portal]);
}

// ── Host classification ──────────────────────────────────────────────────────

export interface HostResolution {
  portal: PortalKey;
  /** Only the five exact LIVE hostnames are isolated. */
  isolated: boolean;
}

/**
 * Normalise a hostname for comparison, or "" when it is not a hostname.
 * Only a trailing ":<port>" is tolerated; "@", "/", "\", whitespace or a second
 * colon normalise to "", which matches nothing (the userinfo trick
 * `admin.pawtenant.com:8080@evil.tld` must never pass an allowlist check).
 */
export function normaliseHost(hostname: string): string {
  const raw = (hostname || "").trim().toLowerCase().replace(/\.$/, "");
  const withoutPort = raw.replace(/:\d+$/, "");
  if (!/^[a-z0-9.-]+$/.test(withoutPort)) return "";
  return withoutPort;
}

/**
 * Preview deployments, *.vercel.app, www (Vercel 308s it first), localhost and
 * any unknown host resolve to the public portal, NOT isolated: they serve
 * every route and are never redirected, so previews stay fully usable.
 */
export function resolveHost(hostname: string): HostResolution {
  const host = normaliseHost(hostname);
  for (const key of Object.keys(LIVE_PORTAL_HOSTS) as PortalKey[]) {
    if (host === LIVE_PORTAL_HOSTS[key]) return { portal: key, isolated: true };
  }
  return { portal: "public", isolated: false };
}

/** Every hostname a LIVE redirect may target. */
export const REDIRECT_ALLOWLIST: readonly string[] = Object.values(LIVE_PORTAL_HOSTS);

/** Exact allowlist membership — never suffix matching. */
export function isAllowedRedirectHost(hostname: string): boolean {
  const host = normaliseHost(hostname);
  return host !== "" && REDIRECT_ALLOWLIST.includes(host);
}

export interface RedirectTarget {
  host: string;
  status: 307 | 308;
}

/**
 * Where a request for `pathname` arriving on `hostname` belongs, or null when
 * it stays: non-isolated host, shared/infrastructure path, a path this host
 * serves, or an old pawtenant.com portal address while legacy redirects are
 * "off".
 */
export function redirectTarget(
  hostname: string,
  pathname: string,
  mode: LegacyRedirectMode = LEGACY_PORTAL_REDIRECTS,
): RedirectTarget | null {
  const arrived = resolveHost(hostname);
  if (!arrived.isolated) return null;
  const owner = ownerPortalForPath(pathname);
  if (owner === null || servedOn(arrived.portal, pathname)) return null;

  const host = LIVE_PORTAL_HOSTS[owner];
  if (!isAllowedRedirectHost(host)) return null;

  if (arrived.portal === "public") {
    if (mode === "off") return null;
    return { host, status: mode === "permanent" ? 308 : 307 };
  }
  return { host, status: 307 };
}

/**
 * Query keys that carry an authentication credential. A request holding one is
 * never re-hosted: the credential would reach an origin the link was not issued
 * for. (The routes that receive them are shared anyway.)
 */
export const AUTH_QUERY_KEYS: readonly string[] = [
  "code",
  "token",
  "token_hash",
  "access_token",
  "refresh_token",
  "confirmation_token",
  "recovery_token",
];

export function carriesAuthCredential(search: string): boolean {
  const params = new URLSearchParams(search);
  for (const key of AUTH_QUERY_KEYS) if (params.has(key)) return true;
  return false;
}

/** Whether portal links built on `hostname` should point at the portal hosts. */
export function portalLinksActive(hostname: string, mode: LegacyRedirectMode = LEGACY_PORTAL_REDIRECTS): boolean {
  const arrived = resolveHost(hostname);
  if (!arrived.isolated) return false;
  return arrived.portal !== "public" || mode !== "off";
}
